import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { History, Euro, Calendar, Wrench, Plus, Trash2, AlertCircle, CheckCircle } from "lucide-react";

/**
 * Section pour gérer l'historique des aides déjà perçues
 * Permet de calculer l'enveloppe restante
 */
export default function AidHistorySection({ 
  onHistoryUpdate,
  incomeCategory 
}) {
  const [isOpen, setIsOpen] = useState(false);
  const [history, setHistory] = useState([]);
  const [newEntry, setNewEntry] = useState({
    year: new Date().getFullYear() - 1,
    program: "maprimerenov",
    work_type: "",
    amount: "",
  });

  // Plafonds MaPrimeRénov' par catégorie
  const MPR_CEILINGS = {
    bleu: 11000,
    jaune: 9000,
    violet: 6000,
    rose: 0
  };

  const ceiling = MPR_CEILINGS[incomeCategory] || 6000;
  const totalReceived = history.reduce((sum, h) => sum + (parseInt(h.amount) || 0), 0);
  const remaining = Math.max(0, ceiling - totalReceived);

  const addEntry = () => {
    if (newEntry.amount && newEntry.work_type) {
      setHistory([...history, { ...newEntry, id: Date.now() }]);
      setNewEntry({ ...newEntry, work_type: "", amount: "" });
      if (onHistoryUpdate) {
        onHistoryUpdate([...history, newEntry]);
      }
    }
  };

  const removeEntry = (id) => {
    const updated = history.filter(h => h.id !== id);
    setHistory(updated);
    if (onHistoryUpdate) {
      onHistoryUpdate(updated);
    }
  };

  const programs = [
    { value: "maprimerenov", label: "MaPrimeRénov'" },
    { value: "cee", label: "Prime CEE" },
    { value: "anah", label: "Aide ANAH (Habiter Mieux)" },
    { value: "local", label: "Aide locale / régionale" },
    { value: "other", label: "Autre aide" },
  ];

  const workTypes = [
    "Isolation des combles",
    "Isolation des murs",
    "Pompe à chaleur",
    "Chaudière",
    "Fenêtres",
    "VMC",
    "Chauffe-eau",
    "Audit énergétique",
    "Autre",
  ];

  return (
    <>
      {/* Carte d'info */}
      <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-4">
        <div className="flex items-start gap-3">
          <div className="w-10 h-10 rounded-full bg-amber-100 flex items-center justify-center flex-shrink-0">
            <History className="h-5 w-5 text-amber-600" />
          </div>
          <div className="flex-1">
            <h3 className="font-semibold text-slate-800 mb-1">Avez-vous déjà perçu des aides ?</h3>
            <p className="text-sm text-slate-600 mb-3">
              Indiquez les aides déjà reçues pour calculer votre enveloppe restante.
            </p>
            
            {history.length > 0 ? (
              <div className="bg-amber-50 rounded-lg p-3 mb-3">
                <div className="flex justify-between items-center">
                  <span className="text-sm font-medium text-amber-800">Déjà perçu</span>
                  <span className="font-bold text-amber-700">{totalReceived.toLocaleString('fr-FR')} €</span>
                </div>
                <div className="flex justify-between items-center mt-1">
                  <span className="text-sm text-amber-700">Enveloppe restante (MPR)</span>
                  <span className="font-bold text-[#1A4D2E]">{remaining.toLocaleString('fr-FR')} €</span>
                </div>
              </div>
            ) : (
              <div className="bg-green-50 rounded-lg p-3 mb-3">
                <p className="text-sm text-green-700 flex items-center gap-2">
                  <CheckCircle className="h-4 w-4" />
                  Enveloppe complète disponible : <strong>{ceiling.toLocaleString('fr-FR')} €</strong>
                </p>
              </div>
            )}

            <Button 
              variant="outline" 
              size="sm"
              onClick={() => setIsOpen(true)}
              className="w-full"
            >
              <Plus className="h-4 w-4 mr-2" />
              {history.length > 0 ? "Modifier l'historique" : "Déclarer des aides perçues"}
            </Button>
          </div>
        </div>
      </div>

      {/* Modal */}
      <Dialog open={isOpen} onOpenChange={setIsOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <History className="h-5 w-5 text-amber-600" />
              Historique des aides perçues
            </DialogTitle>
          </DialogHeader>

          <div className="space-y-4">
            {/* Info plafond */}
            <div className="bg-slate-50 rounded-lg p-3 text-sm">
              <p className="text-slate-600">
                <strong>Plafond MaPrimeRénov' ({incomeCategory}) :</strong>{" "}
                <span className="text-[#1A4D2E] font-bold">{ceiling.toLocaleString('fr-FR')} €</span>
              </p>
              <p className="text-xs text-slate-500 mt-1">
                Ce plafond est réinitialisé tous les 5 ans.
              </p>
            </div>

            {/* Liste des aides déclarées */}
            {history.length > 0 && (
              <div className="space-y-2">
                <h4 className="text-sm font-medium text-slate-700">Aides déclarées</h4>
                {history.map((h) => (
                  <div key={h.id} className="flex items-center justify-between bg-slate-50 rounded-lg p-3">
                    <div>
                      <p className="text-sm font-medium">{h.work_type}</p>
                      <p className="text-xs text-slate-500">
                        {programs.find(p => p.value === h.program)?.label} • {h.year}
                      </p>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="font-bold text-slate-700">{parseInt(h.amount).toLocaleString('fr-FR')} €</span>
                      <button 
                        onClick={() => removeEntry(h.id)}
                        className="text-red-500 hover:text-red-700 p-1"
                      >
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </div>
                  </div>
                ))}

                <div className="bg-amber-50 rounded-lg p-3 mt-2">
                  <div className="flex justify-between text-sm">
                    <span className="text-amber-800">Total perçu</span>
                    <span className="font-bold text-amber-700">{totalReceived.toLocaleString('fr-FR')} €</span>
                  </div>
                  <div className="flex justify-between text-sm mt-1">
                    <span className="text-[#1A4D2E]">Reste disponible</span>
                    <span className="font-bold text-[#1A4D2E]">{remaining.toLocaleString('fr-FR')} €</span>
                  </div>
                </div>
              </div>
            )}

            {/* Formulaire d'ajout */}
            <div className="border-t pt-4">
              <h4 className="text-sm font-medium text-slate-700 mb-3">Ajouter une aide</h4>
              
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-2">
                  <Label htmlFor="year" className="text-xs">Année</Label>
                  <select
                    id="year"
                    value={newEntry.year}
                    onChange={(e) => setNewEntry({ ...newEntry, year: parseInt(e.target.value) })}
                    className="w-full h-10 px-3 border border-slate-200 rounded-md text-sm"
                  >
                    {[...Array(10)].map((_, i) => {
                      const year = new Date().getFullYear() - i;
                      return <option key={year} value={year}>{year}</option>;
                    })}
                  </select>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="program" className="text-xs">Programme</Label>
                  <select
                    id="program"
                    value={newEntry.program}
                    onChange={(e) => setNewEntry({ ...newEntry, program: e.target.value })}
                    className="w-full h-10 px-3 border border-slate-200 rounded-md text-sm"
                  >
                    {programs.map(p => (
                      <option key={p.value} value={p.value}>{p.label}</option>
                    ))}
                  </select>
                </div>
              </div>

              <div className="space-y-2 mt-3">
                <Label htmlFor="work_type" className="text-xs">Type de travaux</Label>
                <select
                  id="work_type"
                  value={newEntry.work_type}
                  onChange={(e) => setNewEntry({ ...newEntry, work_type: e.target.value })}
                  className="w-full h-10 px-3 border border-slate-200 rounded-md text-sm"
                >
                  <option value="">Sélectionner...</option>
                  {workTypes.map(w => (
                    <option key={w} value={w}>{w}</option>
                  ))}
                </select>
              </div>

              <div className="space-y-2 mt-3">
                <Label htmlFor="amount" className="text-xs">Montant perçu (€)</Label>
                <Input
                  id="amount"
                  type="number"
                  value={newEntry.amount}
                  onChange={(e) => setNewEntry({ ...newEntry, amount: e.target.value })}
                  placeholder="Ex: 3000"
                  className="h-10"
                />
              </div>

              <Button 
                onClick={addEntry}
                disabled={!newEntry.work_type || !newEntry.amount}
                className="w-full mt-3 bg-[#1A4D2E] hover:bg-[#143d24]"
              >
                <Plus className="h-4 w-4 mr-2" />
                Ajouter
              </Button>
            </div>

            <Button 
              variant="outline"
              onClick={() => setIsOpen(false)}
              className="w-full"
            >
              Fermer
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}
